import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addInputsFromRefurbishment41_5aLCA,
  deleteInputsFromRefurbishment41_5aLCA,
  getAllInputsFromRefurbishment41_5aLCA,
  updateInputsFromRefurbishment41_5aLCA,
} from "../../../../../../apis/Product/UseStageFlow41Api";
import {
  getProductMaterialUsage34_1aDropdown,
  getProductEnergyInputMaintenance41_2aDropdown,
  getProductWaterConsumptionMaintenance41_2aDropdown,
} from "../../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const InputsFromRefurbishment41_5aTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [energyDropdown, setEnergyDropdown] = useState([]);
  const [waterDropdown, setWaterDropdown] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [unitEnergy, setUnitEnergy] = useState({});
  const [unitWater, setUnitWater] = useState({});

  // Fetch dropdowns - using same dropdowns as 41.2a
  useEffect(() => {
    getProductMaterialUsage34_1aDropdown({
      setDropdown: setMaterialDropdown,
    });
    getProductEnergyInputMaintenance41_2aDropdown({
      setDropdown: setEnergyDropdown,
    });
    getProductWaterConsumptionMaintenance41_2aDropdown({
      setDropdown: setWaterDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllInputsFromRefurbishment41_5aLCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when dropdowns or data change
  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      const newUnitEnergy = {};
      const newUnitWater = {};
      const newFiles = {};

      data.forEach((item) => {
        if (item.material_supporting_refurbishment) {
          const material = materialDropdown.find(
            (m) =>
              m.code === item.material_supporting_refurbishment ||
              m.value === item.material_supporting_refurbishment ||
              m.id === item.material_supporting_refurbishment
          );
          if (material) {
            newUnitMaterials[item.id] = material.unit || "xx";
          }
        }

        if (item.energy_input_during_refurbishment) {
          const energy = energyDropdown.find(
            (e) =>
              e.code === item.energy_input_during_refurbishment ||
              e.value === item.energy_input_during_refurbishment ||
              e.id === item.energy_input_during_refurbishment
          );
          if (energy) {
            newUnitEnergy[item.id] = energy.unit || "xx";
          }
        }

        if (item.water_consumption_during_refurbishment) {
          const water = waterDropdown.find(
            (w) =>
              w.code === item.water_consumption_during_refurbishment ||
              w.value === item.water_consumption_during_refurbishment ||
              w.id === item.water_consumption_during_refurbishment
          );
          if (water) {
            newUnitWater[item.id] = water.unit || "xx";
          }
        }

      });

      setUnitMaterials(newUnitMaterials);
      setUnitEnergy(newUnitEnergy);
      setUnitWater(newUnitWater);
    }
  }, [data, materialDropdown, energyDropdown, waterDropdown]);


  const handleEdit = useCallback(
    (editedItem) => {
      const materialCode =
        typeof editedItem.material_supporting_refurbishment === "string"
          ? editedItem.material_supporting_refurbishment
          : editedItem.material_supporting_refurbishment?.value ||
            editedItem.material_supporting_refurbishment?.code ||
            null;

      const energyCode =
        typeof editedItem.energy_input_during_refurbishment === "string"
          ? editedItem.energy_input_during_refurbishment
          : editedItem.energy_input_during_refurbishment?.value ||
            editedItem.energy_input_during_refurbishment?.code ||
            null;

      const waterCode =
        typeof editedItem.water_consumption_during_refurbishment === "string"
          ? editedItem.water_consumption_during_refurbishment
          : editedItem.water_consumption_during_refurbishment?.value ||
            editedItem.water_consumption_during_refurbishment?.code ||
            null;

      updateInputsFromRefurbishment41_5aLCA({
        productId,
        rowId: editedItem.id,
        name_refurbishment_process: editedItem.name_refurbishment_process || null,
        describe_refurbishment_process: editedItem.describe_refurbishment_process || null,
        material_supporting_refurbishment: materialCode,
        quantity_per_fu_material: editedItem.quantity_per_fu_material || 0,
        energy_input_during_refurbishment: energyCode,
        quantity_per_fu_energy: editedItem.quantity_per_fu_energy || 0,
        water_consumption_during_refurbishment: waterCode,
        quantity_per_fu_water: editedItem.quantity_per_fu_water || 0,
        refurbishment_processes_per_fu_lifetime: editedItem.refurbishment_processes_per_fu_lifetime || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-refurbishment-41-5-totals'));
        },
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialCode =
        typeof newItem.material_supporting_refurbishment === "string"
          ? newItem.material_supporting_refurbishment
          : newItem.material_supporting_refurbishment?.value ||
            newItem.material_supporting_refurbishment?.code ||
            null;

      const energyCode =
        typeof newItem.energy_input_during_refurbishment === "string"
          ? newItem.energy_input_during_refurbishment
          : newItem.energy_input_during_refurbishment?.value ||
            newItem.energy_input_during_refurbishment?.code ||
            null;

      const waterCode =
        typeof newItem.water_consumption_during_refurbishment === "string"
          ? newItem.water_consumption_during_refurbishment
          : newItem.water_consumption_during_refurbishment?.value ||
            newItem.water_consumption_during_refurbishment?.code ||
            null;

      addInputsFromRefurbishment41_5aLCA({
        productId,
        name_refurbishment_process: newItem.name_refurbishment_process || null,
        describe_refurbishment_process: newItem.describe_refurbishment_process || null,
        material_supporting_refurbishment: materialCode,
        quantity_per_fu_material: newItem.quantity_per_fu_material || 0,
        energy_input_during_refurbishment: energyCode,
        quantity_per_fu_energy: newItem.quantity_per_fu_energy || 0,
        water_consumption_during_refurbishment: waterCode,
        quantity_per_fu_water: newItem.quantity_per_fu_water || 0,
        refurbishment_processes_per_fu_lifetime: newItem.refurbishment_processes_per_fu_lifetime || 0,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-refurbishment-41-5-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteInputsFromRefurbishment41_5aLCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-refurbishment-41-5-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);


  const columns = useMemo(
    () => [
      {
        id: "name_refurbishment_process",
        accessor: "name_refurbishment_process",
        header: `${t("NAME_REFURBISHMENT_PROCESS") || "Name refurbishment process"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "describe_refurbishment_process",
        accessor: "describe_refurbishment_process",
        header: `${t("DESCRIBE_REFURBISHMENT_PROCESS") || "Describe refurbishment process"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "material_supporting_refurbishment",
        accessor: "material_supporting_refurbishment",
        header: `${t("MATERIAL_SUPPORTING_REFURBISHMENT") || "Material supporting the refurbishment process"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_MATERIAL") || "Select material...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material_supporting_refurbishment",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_material",
        accessor: "quantity_per_fu_material",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material_supporting_refurbishment",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "energy_input_during_refurbishment",
        accessor: "energy_input_during_refurbishment",
        header: `${t("ENERGY_INPUT_DURING_REFURBISHMENT") || "Energy input during refurbishment"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_ENERGY_TYPE") || "Select energy type...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "energy_input_during_refurbishment",
            energyDropdown,
            unitEnergy,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_energy",
        accessor: "quantity_per_fu_energy",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "energy_input_during_refurbishment",
            energyDropdown,
            unitEnergy,
            rowId
          ),
      },
      {
        id: "water_consumption_during_refurbishment",
        accessor: "water_consumption_during_refurbishment",
        header: `${t("WATER_CONSUMPTION_DURING_REFURBISHMENT") || "Water consumption during refurbishment"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_WATER_TYPE") || "Select water type...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "water_consumption_during_refurbishment",
            waterDropdown,
            unitWater,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_water",
        accessor: "quantity_per_fu_water",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "water_consumption_during_refurbishment",
            waterDropdown,
            unitWater,
            rowId
          ),
      },
      {
        id: "refurbishment_processes_per_fu_lifetime",
        accessor: "refurbishment_processes_per_fu_lifetime",
        header: `${t("REFURBISHMENT_PROCESSES_PER_FU_LIFETIME") || "Refurbishment processes per functional unit during lifetime"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitMaterials,
      unitEnergy,
      unitWater,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      material_supporting_refurbishment: materialDropdown || [],
      energy_input_during_refurbishment: energyDropdown || [],
      water_consumption_during_refurbishment: waterDropdown || [],
    }),
    [materialDropdown, energyDropdown, waterDropdown]
  );

  // Custom input for new row describe refurbishment process

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default InputsFromRefurbishment41_5aTable;

